import config from '../config/config';
import AuthService from './AuthService';

class ApiService {
  // Callback invoked when a 402 (subscription required) response is received
  static onSubscriptionRequired = null;
  // Callback invoked when authentication fails and cannot be recovered
  static onAuthFailure = null;

  static async makeRequest(url, options = {}) {
    const isFormData = options.body instanceof FormData;
    const authHeaders = AuthService.getAuthHeaders();

    // Don't set Content-Type for FormData — browser sets it with boundary
    if (isFormData) {
      delete authHeaders['Content-Type'];
    }

    try {
      const response = await fetch(`${config.API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...authHeaders,
          ...options.headers
        }
      });

      if (response.status === 402) {
        if (ApiService.onSubscriptionRequired) {
          ApiService.onSubscriptionRequired();
        }
        throw new Error('Subscription required');
      }

      if (response.status === 401 || response.status === 403) {
        try {
          await AuthService.refreshAccessToken();
        } catch {
          if (ApiService.onAuthFailure) ApiService.onAuthFailure();
          throw new Error('Authentication failed. Please log in again.');
        }
        const retryHeaders = AuthService.getAuthHeaders();
        if (isFormData) {
          delete retryHeaders['Content-Type'];
        }
        const retryResponse = await fetch(`${config.API_BASE_URL}${url}`, {
          ...options,
          headers: {
            ...retryHeaders,
            ...options.headers
          }
        });
        if (retryResponse.status === 401 || retryResponse.status === 403) {
          if (ApiService.onAuthFailure) ApiService.onAuthFailure();
          throw new Error('Authentication failed. Please log in again.');
        }
        return retryResponse;
      }

      return response;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // ── Family & Relatives ──

  static async getFamiliesAndRelatives() {
    const response = await this.makeRequest('/family', { method: 'GET' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch families and relatives');
    }
    return response.json();
  }

  static async updateFamilyProperties(familyUuid, payload) {
    const response = await this.makeRequest('/family/updateProperties', {
      method: 'PUT',
      body: JSON.stringify({
        uuid: familyUuid,
        payload: { action: 'update', ...payload }
      })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || body.message || 'Failed to update family properties');
    }
    return response.json();
  }

  static async removeFamilyMember(familyUuid, memberUuid) {
    const response = await this.makeRequest('/family', {
      method: 'PUT',
      body: JSON.stringify({
        uuid: familyUuid,
        familyPayload: { action: 'remove', member: { uuid: memberUuid } }
      })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || body.message || 'Failed to remove member');
    }
    return response.json();
  }

  static async deleteFamily(familyUuid) {
    const response = await this.makeRequest('/family', {
      method: 'DELETE',
      body: JSON.stringify({ uuid: familyUuid })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || body.message || 'Failed to delete family');
    }
    return response.json();
  }

  // ── Content ──

  static async getContentForRelative(relativeUuid, lastCheckTime = new Date(0)) {
    const response = await this.makeRequest('/checkForContent', {
      method: 'POST',
      body: JSON.stringify({
        lastCheckTime: lastCheckTime.toISOString(),
        relativeUuids: [relativeUuid]
      })
    });
    if (!response.ok) throw new Error('Failed to fetch content');
    return response.json();
  }

  static async getContentForMultipleRelatives(relativeUuids, lastCheckTime = new Date(0)) {
    const response = await this.makeRequest('/checkForContent', {
      method: 'POST',
      body: JSON.stringify({
        lastCheckTime: lastCheckTime.toISOString(),
        relativeUuids
      })
    });
    if (!response.ok) throw new Error('Failed to fetch content');
    return response.json();
  }

  // ── Paginated Content ──

  static async getContentList(relativeUuids, lastCheckTime = new Date(0)) {
    const response = await this.makeRequest('/checkForContentList', {
      method: 'POST',
      body: JSON.stringify({
        lastCheckTime: lastCheckTime.toISOString(),
        relativeUuids
      })
    });
    if (!response.ok) throw new Error('Failed to fetch content list');
    return response.json();
  }

  static async getContentBatch(contentUuids) {
    const response = await this.makeRequest('/checkForContentBatch', {
      method: 'POST',
      body: JSON.stringify({ contentUuids })
    });
    if (!response.ok) throw new Error('Failed to fetch content batch');
    return response.json();
  }

  static async deleteContent(contentUuid) {
    const response = await this.makeRequest('/contentUpdates', {
      method: 'DELETE',
      body: JSON.stringify({ contentUuid })
    });
    if (!response.ok) throw new Error('Failed to delete content');
    return response.json();
  }

  static async refreshContentUrls(contentUuid) {
    const response = await this.makeRequest('/contentUpdates', {
      method: 'PUT',
      body: JSON.stringify({ contentUuid })
    });
    if (!response.ok) throw new Error('Failed to refresh content URLs');
    return response.json();
  }

  // ── Engagement: Favorites ──

  static async toggleFavorite(contentUuid, action) {
    const response = await this.makeRequest('/contentUpdates/modifyFavorites', {
      method: 'POST',
      body: JSON.stringify({ contentUuid, action })
    });
    if (!response.ok) throw new Error('Failed to update favorite');
    return response.json();
  }

  // ── Engagement: Likes ──

  static async toggleLike(contentUuid) {
    const response = await this.makeRequest('/contentUpdates/like', {
      method: 'POST',
      body: JSON.stringify({ contentUuid })
    });
    if (!response.ok) throw new Error('Failed to toggle like');
    return response.json();
  }

  static async removeLike(contentUuid) {
    const response = await this.makeRequest('/contentUpdates/like', {
      method: 'DELETE',
      body: JSON.stringify({ contentUuid })
    });
    if (!response.ok) throw new Error('Failed to remove like');
    return response.json();
  }

  // ── Engagement: Comments ──

  static async addComment(contentUuid, commentText) {
    const response = await this.makeRequest('/contentUpdates/comment', {
      method: 'POST',
      body: JSON.stringify({ contentUuid, commentText })
    });
    if (!response.ok) throw new Error('Failed to add comment');
    return response.json();
  }

  static async deleteComment(contentUuid, commentUuid) {
    const response = await this.makeRequest('/contentUpdates/comment', {
      method: 'DELETE',
      body: JSON.stringify({ contentUuid, commentUuid })
    });
    if (!response.ok) throw new Error('Failed to delete comment');
    return response.json();
  }

  // ── Virtual Chat (Selfie Mode) ──

  static async sendVirtualChatMessage(relativeUuid, conversationUuid, messages) {
    const response = await this.makeRequest('/virtualChat', {
      method: 'POST',
      body: JSON.stringify({ relativeUuid, conversationUuid, messages })
    });
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('No transcript context found. More videos need to be recorded.');
      }
      if (response.status === 412) {
        throw new Error('Not enough videos recorded yet for Selfie Mode.');
      }
      throw new Error('Failed to send chat message');
    }
    return response.json();
  }

  static async checkChatReadiness(relativeUuid) {
    const response = await this.makeRequest(
      `/virtualChat/readiness?relativeUuid=${encodeURIComponent(relativeUuid)}`,
      { method: 'GET' }
    );
    if (!response.ok) throw new Error('Failed to check chat readiness');
    return response.json();
  }

  static async submitChatFeedback(conversationUuid, messageIndex, rating, feedbackText = '') {
    const response = await this.makeRequest('/virtualChat/feedback', {
      method: 'PUT',
      body: JSON.stringify({ conversationUuid, messageIndex, rating, feedbackText })
    });
    if (!response.ok) throw new Error('Failed to submit feedback');
    return response.json();
  }

  // ── User Profile ──

  static async updateUserProperties(payload) {
    const response = await this.makeRequest('/user/updateProperties', {
      method: 'PUT',
      body: JSON.stringify({ payload })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || body.message || 'Failed to update user properties');
    }
    return response.json();
  }

  static async uploadProfilePicture(formData) {
    const response = await this.makeRequest('/user/uploadProfilePicture', {
      method: 'POST',
      body: formData
    });
    if (!response.ok) throw new Error('Failed to upload profile picture');
    return response.json();
  }

  static async updateVerifyUserAge(userBirthYear, userBirthMonth, userBirthDay, userName, userIanaTimezone) {
    const response = await this.makeRequest('/user/updateVerifyUserAge', {
      method: 'PUT',
      body: JSON.stringify({ userBirthYear, userBirthMonth, userBirthDay, userName, userIanaTimezone })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || body.message || 'Failed to update birthday');
    }
    return response.json();
  }

  static async deleteUser() {
    const response = await this.makeRequest('/user/deleteUser', {
      method: 'PUT'
    });
    if (!response.ok) throw new Error('Failed to delete account');
    return response.json();
  }

  // ── Relative Updates ──

  static async updateRelativeRelationship(relativeUuid, relationship) {
    const response = await this.makeRequest('/user/updateProperties', {
      method: 'PUT',
      body: JSON.stringify({
        payload: {
          action: 'update',
          relativeUpdates: [{ uuid: relativeUuid, relationship }]
        }
      })
    });
    if (!response.ok) throw new Error('Failed to update relationship');
    return response.json();
  }

  // ── Recording: Questions ──

  static async getQuestions(lastCheckTime = new Date(0)) {
    const response = await this.makeRequest('/checkForQuestions', {
      method: 'POST',
      body: JSON.stringify({ lastCheckTime: lastCheckTime.toISOString() })
    });
    if (!response.ok) throw new Error('Failed to fetch questions');
    return response.json();
  }

  // ── Recording: Upload ──

  static async uploadContent(formData) {
    const attempt = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000); // 5 min timeout
      try {
        const response = await this.makeRequest('/uploadFile', {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Failed to upload content');
        return response.json();
      } finally {
        clearTimeout(timeoutId);
      }
    };

    try {
      return await attempt();
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      console.warn('Upload failed, retrying once:', err.message);
      return await attempt();
    }
  }

  static async getUploadStatus(jobId) {
    const response = await this.makeRequest(`/uploadFile/status/${encodeURIComponent(jobId)}`, {
      method: 'GET'
    });
    if (!response.ok) throw new Error('Failed to get upload status');
    return response.json();
  }

  // ── Subscription ──

  static async getSubscriptionInfo() {
    const response = await this.makeRequest('/subscription/getUserSubscriptionInfo', {
      method: 'POST'
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to fetch subscription info');
    }
    return response.json();
  }

  static async getSubscriptionCatalog() {
    const response = await this.makeRequest('/subscription/catalog', {
      method: 'GET'
    });
    if (!response.ok) throw new Error('Failed to fetch subscription catalog');
    return response.json();
  }

  static async checkSubscriptionStatus() {
    const response = await this.makeRequest('/subscription/hasValidSubscription', {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to check subscription status');
    return response.json();
  }

  static async createStripeCheckout(planId, successUrl, cancelUrl) {
    const response = await this.makeRequest('/subscription/stripe-checkout', {
      method: 'POST',
      body: JSON.stringify({ planId, successUrl, cancelUrl })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to create checkout session');
    }
    return response.json();
  }

  static async createStripePortal() {
    const response = await this.makeRequest('/subscription/stripe-portal', {
      method: 'POST'
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to create billing portal session');
    }
    return response.json();
  }

  static async uncancelSubscription() {
    const response = await this.makeRequest('/subscription/stripe-uncancel', {
      method: 'POST'
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to reactivate subscription');
    }
    return response.json();
  }

  static async cancelSubscription() {
    const response = await this.makeRequest('/subscription/stripe-cancel', {
      method: 'POST'
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to cancel subscription');
    }
    return response.json();
  }

  static async changeSubscriptionPlan(planId) {
    const response = await this.makeRequest('/subscription/stripe-change-plan', {
      method: 'POST',
      body: JSON.stringify({ planId })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to change subscription plan');
    }
    return response.json();
  }

  static async createTrialSubscription() {
    const response = await this.makeRequest('/subscription/createTrial', {
      method: 'POST'
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to create trial subscription');
    }
    return response.json();
  }

  static async getBackupUrls() {
    const response = await this.makeRequest('/uploadFile/retrieveBackupUrls', {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to retrieve backup URLs');
    return response.json();
  }

  // ── Recording: Story Mode ──

  static async startStorySession(relativeUuid, forceNew = false) {
    const response = await this.makeRequest('/story/start', {
      method: 'POST',
      body: JSON.stringify({ relativeUuid, forceNew })
    });
    if (!response.ok) throw new Error('Failed to start story session');
    return response.json();
  }

  static async pollStoryNextQuestion(sessionUuid) {
    const response = await this.makeRequest(`/story/next-question/${encodeURIComponent(sessionUuid)}`, {
      method: 'GET'
    });
    // 202 = still processing, 200 = ready
    return { status: response.status, data: await response.json() };
  }
}

export default ApiService;
